// V4.2 — DealAlert AI Market Intelligence (Modus B).
// AI-Home (Today's Insight + 6 action cards) instead of an empty chat;
// desktop >=1280px gets the two-column layout with the Market Canvas
// (widgets of the latest answer, rendered large); ?ask= deep-links from
// Heatmap/Deal-Advisor auto-send a prefilled question.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Bot, Brain, Send, RotateCcw, Sparkles, Mic, MicOff, PanelRight,
} from "lucide-react";
import {
  fetchAssistantConversation,
  fetchAssistantInsights,
  fetchAssistantQuickActions,
  fetchAiGreeting,
  sendAssistantTurn,
} from "../api/aiApi";
import { ChatWidgets } from "../components/ai/ChatWidgets";
import { CanvasBoard } from "../components/ai/CanvasBoard";
import { MarketBriefCard } from "../components/ai/MarketBriefCard";
import { AnalysisShareButton } from "../components/ai/AnalysisShareButton";
import { InsightBoard } from "../components/ai/InsightBoard";
import { ActionCards } from "../components/ai/ActionCards";
import { MemorySuggestionChip } from "../components/ai/MemorySuggestionChip";
import { ProUpgradeButton } from "../components/billing/PaywallComponents";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useSpeechRecognition } from "../hooks/useSpeechRecognition";
import SpotlightTour from "../tour/SpotlightTour";
import { useTour } from "../tour/useTour";
import { firstUrl, tokenizeMessage } from "../lib/linkify";
import { LinkAnalysisStatus } from "../components/ai/LinkAnalysisStatus";

function newRequestId() {
  return `ast-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function renderBold(text) {
  // Assistant replies use **…** for key figures — render as <strong>.
  const parts = String(text || "").split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold text-zinc-50">{p}</strong>
    ) : (
      p
    ),
  );
}

// Master-Hotfix P0 — bare URLs and markdown links render as SHORT
// labels (`mobile.de · Inserat 461285717`) with the full href intact,
// so a 500-char share link can never widen the chat.
function renderRich(text) {
  return tokenizeMessage(text).map((tok, i) =>
    tok.type === "link" ? (
      <a
        key={i}
        href={tok.href}
        target="_blank"
        rel="noreferrer"
        data-testid="ai-chat-short-link"
        className="break-all text-amber-300 underline decoration-amber-500/40 underline-offset-2 transition-colors hover:text-amber-200"
      >
        {tok.label}
      </a>
    ) : (
      <React.Fragment key={i}>{renderBold(tok.value)}</React.Fragment>
    ),
  );
}

function Bubble({ role, children }) {
  const isUser = role === "user";
  return (
    <div className={`flex min-w-0 ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`ai-bubble max-w-[85%] sm:max-w-[75%] min-w-0 rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${
          isUser
            ? "bg-amber-500/15 border border-amber-500/25 text-zinc-100"
            : "bg-zinc-900/80 border border-zinc-800 text-zinc-200"
        }`}
      >
        {typeof children === "string" ? renderRich(children) : children}
      </div>
    </div>
  );
}

function ThinkingStatus({ rescuing }) {
  // V4.13 Polish — turns take 10-40s: rotate honest progress copy so
  // the wait feels intentional instead of frozen.
  const { t } = useTranslation("ai");
  const phases = t("assistant.thinkingPhases", { returnObjects: true });
  const list = Array.isArray(phases) && phases.length
    ? phases
    : [t("assistant.thinking")];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % list.length), 6000);
    return () => clearInterval(id);
  }, [list.length]);
  return (
    <span className="ml-1 text-xs text-zinc-500" data-testid="ai-thinking-status">
      {rescuing ? t("assistant.stillWorking") : list[idx]}
    </span>
  );
}

export default function AIChatPage() {
  const { t, i18n } = useTranslation("ai");
  const { t: tc } = useTranslation("common");
  const aiTour = useTour("tour_ai_done");
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const composerRef = useRef(null);
  // Reset the auto-grown composer height once the input is cleared
  // (programmatic clear on send does not fire onChange).
  useEffect(() => {
    if (input === "" && composerRef.current) {
      composerRef.current.style.height = "auto";
    }
  }, [input]);
  const [paywalled, setPaywalled] = useState(false);
  const [quota, setQuota] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [rescuing, setRescuing] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const threadRef = useRef(null);
  const hydratedRef = useRef(false);
  const pendingResetRef = useRef(false);
  const askSentRef = useRef(false);
  const messagesLenRef = useRef(0);
  const speech = useSpeechRecognition({
    onTranscript: (text) => setInput((prev) => (prev ? `${prev} ${text}` : text)),
  });

  const convQ = useQuery({
    queryKey: ["ai-assistant-conversation"],
    queryFn: fetchAssistantConversation,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const chipsQ = useQuery({
    queryKey: ["ai-assistant-quick-actions"],
    queryFn: fetchAssistantQuickActions,
    staleTime: 10 * 60 * 1000,
  });
  const insightsQ = useQuery({
    queryKey: ["ai-assistant-insights"],
    queryFn: fetchAssistantInsights,
    staleTime: 30 * 60 * 1000,
  });
  const greetingQ = useQuery({
    queryKey: ["ai-greeting"],
    queryFn: fetchAiGreeting,
    staleTime: 60 * 1000,
    retry: false,
  });

  // Hydrate local messages from the persisted conversation. V4.10
  // Phase 18 — length-based merge instead of the empty-only guard: a
  // remount (Listing-Umweg) hydrates from the react-query cache first;
  // when fresher data with MORE turns lands it must still win,
  // otherwise the last exchange stays invisible until a full reload.
  useEffect(() => {
    if (pendingResetRef.current) return;
    const turns = convQ.data?.turns;
    if (!turns) return;
    setMessages((prev) =>
      turns.length > prev.length
        ? turns.map((turn) => ({
            role: turn.role,
            content: turn.content,
            widgets: turn.widgets || [],
          }))
        : prev,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convQ.data]);

  // V4.23 Workspace — scroll contract: on a NEW exchange the chat pins
  // the latest USER message to the top of the thread so the START of
  // the AI answer is visible (never auto-jump to the very bottom).
  // Initial history hydration still lands at the end once.
  const pinToLatestUserMsg = () => {
    const c = threadRef.current;
    if (!c) return;
    const nodes = c.querySelectorAll('[data-msg-role="user"]');
    const last = nodes[nodes.length - 1];
    if (!last) return;
    const cRect = c.getBoundingClientRect();
    const lRect = last.getBoundingClientRect();
    c.scrollTop += lRect.top - cRect.top - 6;
  };
  useEffect(() => {
    messagesLenRef.current = messages.length;
    if (!messages.length) return;
    const c = threadRef.current;
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      if (c) c.scrollTop = c.scrollHeight;
      return;
    }
    requestAnimationFrame(pinToLatestUserMsg);
  }, [messages.length]);

  // V4.11 Reliability — the backend ALWAYS persists a finished turn,
  // even when the HTTP response is cut by a proxy timeout. Instead of a
  // false error toast we poll the persisted conversation and pull the
  // answer in.
  const tryRescueFromServer = async (schedule = [4000, 7000, 10000]) => {
    setRescuing(true);
    try {
      for (const delay of schedule) {
        await new Promise((r) => setTimeout(r, delay));
        try {
          const conv = await fetchAssistantConversation();
          const turns = conv?.turns || [];
          if (
            turns.length > messagesLenRef.current &&
            turns[turns.length - 1]?.role === "assistant"
          ) {
            setMessages(
              turns.map((turn) => ({
                role: turn.role,
                content: turn.content,
                widgets: turn.widgets || [],
              })),
            );
            qc.setQueryData(["ai-assistant-conversation"], conv);
            return true;
          }
        } catch {
          /* keep polling */
        }
      }
      return false;
    } finally {
      setRescuing(false);
    }
  };

  const sendMutation = useMutation({
    mutationFn: sendAssistantTurn,
    onSuccess: (data, variables) => {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.assistant_message,
          widgets: data.widgets || [],
          followups: data.suggested_followups || [],
        },
      ]);
      // V4.10 Phase 18 — keep the cached conversation snapshot in sync.
      // With staleTime 30s a remount would otherwise hydrate a stale
      // snapshot missing this exchange (last message gone until reload).
      qc.setQueryData(["ai-assistant-conversation"], (old) => ({
        ...(old || {}),
        turns: [
          ...((old && old.turns) || []),
          {
            role: "user",
            content:
              (variables?.message || "").trim() ||
              chipLabel(variables?.quickActionSlug),
            widgets: [],
          },
          {
            role: "assistant",
            content: data.assistant_message,
            widgets: data.widgets || [],
          },
        ],
      }));
      if (data.ai_quota) setQuota(data.ai_quota);
      // V4.12 Phase F — the backend added this turn's data widgets to
      // the persistent canvas: refresh the board.
      if (data.canvas_added) {
        qc.invalidateQueries({ queryKey: ["ai-canvas"] });
      }
      setErrorMsg(null);
    },
    onError: async (err) => {
      const status = err?.response?.status;
      if (status === 402) {
        setPaywalled(true);
        return;
      }
      if (status === 429) {
        setErrorMsg(t("assistant.rateLimited"));
        return;
      }
      // P0 hotfix — 409 request_in_progress: a transparent browser/proxy
      // retry hit the server while the FIRST attempt is still producing
      // the answer. The turn WILL be persisted — poll it in instead of
      // leaving a dead red error until reload.
      if (
        status === 409 &&
        err?.response?.data?.error?.code === "request_in_progress"
      ) {
        const rescued = await tryRescueFromServer([
          3000, 5000, 8000, 10000, 12000, 15000, 15000,
        ]);
        if (rescued) {
          setErrorMsg(null);
          return;
        }
        setErrorMsg(t("assistant.stillWorkingReload"));
        return;
      }
      // Timeout / network cut / gateway error → the answer is most
      // likely still being produced and WILL be persisted: rescue it.
      if (!status || status >= 500 || status === 408) {
        const rescued = await tryRescueFromServer();
        if (rescued) {
          setErrorMsg(null);
          return;
        }
      }
      setErrorMsg(
        err?.response?.data?.error?.message || t("assistant.genericError"),
      );
    },
  });

  const chipLabel = (slug) =>
    (chipsQ.data?.items || []).find((c) => c.slug === slug)?.label || slug;

  const send = (text, quickActionSlug) => {
    const clean = (text || "").trim();
    if (!clean && !quickActionSlug) return;
    setMessages((prev) => [
      ...prev,
      { role: "user", content: clean || chipLabel(quickActionSlug) },
    ]);
    setInput("");
    const resetFlag = pendingResetRef.current;
    pendingResetRef.current = false;
    sendMutation.mutate({
      message: clean || undefined,
      quickActionSlug,
      resetConversation: resetFlag,
      clientRequestId: newRequestId(),
    });
  };

  // Deep-link entry (?ask=...) — from Heatmap regions / Deal-Advisor.
  // V4.8 P0 — hydration race fix: WAIT until the conversation history
  // fetch has settled before auto-sending, otherwise the length-guard
  // above skips hydration and the whole history (incl. its canvas
  // widgets) stays invisible until the next remount.
  const convSettled = convQ.isFetched || convQ.isError;
  useEffect(() => {
    if (!convSettled) return;
    const ask = searchParams.get("ask");
    if (ask && !askSentRef.current) {
      askSentRef.current = true;
      setSearchParams({}, { replace: true });
      send(ask);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, convSettled]);

  const onReset = () => {
    setResetOpen(false);
    setMessages([]);
    setErrorMsg(null);
    pendingResetRef.current = true;
    qc.removeQueries({ queryKey: ["ai-assistant-conversation"] });
  };

  const lastAssistant = useMemo(
    () => [...messages].reverse().find((m) => m.role === "assistant"),
    [messages],
  );
  // Master-Hotfix P0 — while a URL-bearing message is being processed
  // the thread shows the structured Link-Resolver status card instead
  // of the generic thinking dots.
  const lastMsg = messages.length ? messages[messages.length - 1] : null;
  const pendingLinkUrl =
    sendMutation.isPending && lastMsg?.role === "user"
      ? firstUrl(lastMsg.content)
      : null;
  const isEmpty =
    messages.length === 0 && (pendingResetRef.current || !convQ.isLoading);

  if (paywalled) {
    return (
      <div data-testid="ai-chat-paywall" className="max-w-lg mx-auto mt-16 rounded-2xl border border-emerald-500/30 bg-zinc-900/70 p-8 text-center space-y-4">
        <Sparkles className="h-8 w-8 text-emerald-400 mx-auto" />
        <h1 className="text-xl text-zinc-100 font-medium">{t("assistant.paywallHeading")}</h1>
        <p className="text-sm text-zinc-400">{t("assistant.paywallSub")}</p>
        <ProUpgradeButton data-testid="ai-chat-upgrade-button">
          {t("assistant.paywallCta")}
        </ProUpgradeButton>
      </div>
    );
  }

  const composer = (
    <div className="shrink-0 bg-gradient-to-t from-zinc-950 via-zinc-950/95 to-transparent pb-1.5 pt-2">
      {/* V4.7 P1 — trial quota hint: silent until ~5 questions remain */}
      {quota && quota.remaining <= 5 && (
        <div
          data-testid="ai-quota-hint"
          className="mb-2 flex items-center justify-center gap-1.5 text-[11px] text-zinc-500"
        >
          <span>{t("quota.hint", { count: quota.remaining })}</span>
          <Link
            to="/profile"
            data-testid="ai-quota-upgrade-link"
            className="text-amber-400/90 transition-colors hover:text-amber-300"
          >
            {t("quota.cta")}
          </Link>
        </div>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
        className="flex items-end gap-2 rounded-2xl border border-zinc-800 bg-zinc-900/90 p-2 focus-within:border-amber-500/50 transition"
      >
        <textarea
          data-testid="ai-chat-input"
          ref={composerRef}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            // Master-Hotfix P0.2 — auto-grow up to max-h-32 (128px). The
            // composer stays in the flex flow, the thread (flex-1,
            // min-h-0) gives up the height — the workspace height itself
            // never re-measures, so the composer can't be pushed out.
            const el = e.target;
            el.style.height = "auto";
            el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
          rows={1}
          placeholder={t("assistant.placeholder")}
          className="flex-1 resize-none bg-transparent px-2 py-1.5 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none max-h-32"
        />
        {speech.isSupported && (
          <button
            type="button"
            data-testid="ai-chat-mic-btn"
            onClick={speech.toggle}
            className={`h-9 w-9 rounded-xl flex items-center justify-center transition ${
              speech.isListening
                ? "bg-rose-500/20 text-rose-300 animate-pulse"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            {speech.isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </button>
        )}
        <button
          type="submit"
          data-testid="ai-chat-send-btn"
          disabled={sendMutation.isPending || !input.trim()}
          className="h-9 w-9 rounded-xl bg-amber-500 text-zinc-950 flex items-center justify-center hover:bg-amber-400 transition disabled:opacity-40"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
      <p className="text-[10px] text-zinc-600 text-center mt-2">{t("assistant.disclaimer")}</p>
    </div>
  );

  return (
    <div
      data-testid="ai-chat-page"
      className="mx-auto flex min-h-0 w-full flex-1 flex-col xl:grid xl:max-w-7xl xl:grid-cols-[minmax(0,1fr)_400px] xl:gap-8"
    >
      {/* ---- Left column: conversation (own scroll region) ---- */}
      <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col xl:flex-none">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-zinc-800/70 pb-2.5 sm:pb-3">
          <div className="flex items-center gap-2.5 sm:gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-amber-500/30 bg-amber-500/15 sm:h-10 sm:w-10 sm:rounded-2xl">
              <Bot className="h-4 w-4 text-amber-400 sm:h-5 sm:w-5" />
            </div>
            <div>
              <h1 className="text-base font-medium leading-tight text-zinc-100 sm:text-lg">{t("assistant.title")}</h1>
              <p className="hidden text-xs text-zinc-500 sm:block">{t("assistant.subtitle")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {lastAssistant && <AnalysisShareButton />}
            <Link
              to="/ai/memory"
              data-testid="ai-memory-link"
              title={t("memory.title")}
              className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-zinc-500 transition hover:bg-zinc-800/60 hover:text-zinc-300"
            >
              <Brain className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t("memory.navLabel")}</span>
            </Link>
            {messages.length > 0 && (
              <button
                data-testid="ai-chat-reset-btn"
                onClick={() => setResetOpen(true)}
                className="flex items-center gap-1.5 text-xs text-zinc-500 transition hover:text-zinc-300"
              >
                <RotateCcw className="h-3.5 w-3.5" /> {t("assistant.newChat")}
              </button>
            )}
          </div>
        </div>

        {/* Thread / AI-Home — the ONLY scroll region of this column */}
        <div
          ref={threadRef}
          className="min-h-0 flex-1 space-y-4 overflow-y-auto py-5"
          data-testid="ai-chat-thread"
        >
          {/* V4.23 Polish — welcome-back brief lives INSIDE the scroll
              region (compact, collapsible, dismissible — never covers
              chat content, scrolls away naturally). */}
          <MarketBriefCard />
          {isEmpty ? (
            <div className="space-y-6">
              <InsightBoard insights={insightsQ.data} onAsk={(q) => send(q)} />
              <div className="space-y-2 pt-2 text-center">
                <div className="text-2xl font-medium text-zinc-100" data-testid="ai-greeting-headline">
                  {greetingQ.data?.first_name
                    ? t("assistant.helloName", { name: greetingQ.data.first_name })
                    : t("assistant.emptyHeadline")}
                </div>
                <p className="mx-auto max-w-md text-sm text-zinc-500">
                  {t("assistant.advisorIntro")}
                </p>
              </div>
              {greetingQ.data?.new_matches?.count > 0 && greetingQ.data.new_matches.label && (
                <div className="flex justify-center">
                  <button
                    data-testid="ai-new-matches-chip"
                    onClick={() =>
                      send(`${t("assistant.newMatchesCta")} ${greetingQ.data.new_matches.label}`)
                    }
                    className="max-w-md rounded-2xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-left transition hover:bg-emerald-500/20"
                  >
                    <span className="block truncate text-xs text-emerald-300">
                      {t("assistant.newMatches", {
                        count: greetingQ.data.new_matches.count,
                        countDisplay: `${greetingQ.data.new_matches.count}${greetingQ.data.new_matches.capped ? "+" : ""}`,
                        label: greetingQ.data.new_matches.label,
                      })}
                    </span>
                    <span className="mt-0.5 block truncate text-[10px] text-emerald-300/60">
                      {t("assistant.newMatchesMeta", {
                        date: greetingQ.data.new_matches.since
                          ? new Date(greetingQ.data.new_matches.since).toLocaleDateString(
                              i18n.language === "hr" ? "hr-HR" : i18n.language === "en" ? "en-GB" : "de-AT",
                              { day: "numeric", month: "short" },
                            )
                          : "—",
                        basedOn: (greetingQ.data.new_matches.based_on || []).join(" · "),
                      })}
                    </span>
                  </button>
                </div>
              )}
              {greetingQ.data?.last_focus && (
                <div className="flex justify-center">
                  <button
                    data-testid="ai-last-focus-chip"
                    onClick={() =>
                      send(`${t("assistant.lastFocusCta")} ${greetingQ.data.last_focus}`)
                    }
                    className="max-w-md truncate rounded-full border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs text-amber-300 transition hover:bg-amber-500/20"
                  >
                    {t("assistant.lastFocus", { topic: greetingQ.data.last_focus })}{" "}
                    · {t("assistant.lastFocusCta")}
                  </button>
                </div>
              )}
              {greetingQ.data?.suggestions?.length > 0 && (
                <MemorySuggestionChip suggestion={greetingQ.data.suggestions[0]} />
              )}
              <div className="mx-auto grid max-w-lg grid-cols-2 gap-2" data-testid="ai-onboarding-entries">
                {[
                  ["auto", t("assistant.entryAuto")],
                  ["immo", t("assistant.entryImmo")],
                  ["invest", t("assistant.entryInvest")],
                  ["valuation", t("assistant.entryValuation")],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    data-testid={`ai-onboarding-entry-${key}`}
                    onClick={() => send(label)}
                    className="rounded-xl border border-zinc-800 bg-zinc-900/70 px-3 py-2.5 text-sm text-zinc-300 transition hover:border-amber-500/50 hover:text-amber-300"
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div data-testid="ai-chat-quick-actions">
                <ActionCards
                  actions={chipsQ.data?.items || []}
                  onAction={(slug) => send(null, slug)}
                  disabled={sendMutation.isPending}
                />
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} data-msg-role={m.role} className="min-w-0">
                <Bubble role={m.role}>{m.content}</Bubble>
                {m.role === "assistant" && (
                  <ChatWidgets widgets={m.widgets} variant="chat" onSend={send} />
                )}
              </div>
            ))
          )}

          {(sendMutation.isPending || rescuing) &&
            (pendingLinkUrl ? (
              <LinkAnalysisStatus url={pendingLinkUrl} />
            ) : (
            <div className="flex justify-start" data-testid="ai-chat-thinking">
              <div className="flex items-center gap-2 rounded-2xl border border-zinc-800 bg-zinc-900/80 px-4 py-3">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-400" />
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-400 [animation-delay:150ms]" />
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-400 [animation-delay:300ms]" />
                <ThinkingStatus rescuing={rescuing} />
              </div>
            </div>
            ))}

          {!sendMutation.isPending && lastAssistant?.followups?.length > 0 && (
            <div className="flex flex-wrap gap-2 pl-1" data-testid="ai-chat-followups">
              {lastAssistant.followups.map((f) => (
                <button
                  key={f}
                  onClick={() => send(f)}
                  className="rounded-full border border-zinc-700/80 px-3 py-1.5 text-xs text-zinc-400 transition hover:border-amber-500/60 hover:text-amber-300"
                >
                  {f}
                </button>
              ))}
            </div>
          )}

          {errorMsg && (
            <div data-testid="ai-chat-error" className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-2.5 text-sm text-rose-200">
              {errorMsg}
            </div>
          )}
        </div>

        {composer}
      </div>

      {/* ---- Right column: Market Canvas (desktop >=1280px) — own
              scroll region, header stays pinned ---- */}
      <aside
        data-testid="ai-market-canvas"
        className="hidden min-h-0 xl:flex xl:flex-col"
      >
        <div className="flex shrink-0 items-center gap-2 pb-3 pt-1 text-xs uppercase tracking-wide text-zinc-500">
          <PanelRight className="h-3.5 w-3.5" />
          {t("assistant.canvasTitle")}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto pb-4 pr-1">
          <CanvasBoard />
        </div>
      </aside>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent
          data-testid="ai-reset-dialog"
          className="border-zinc-800 bg-zinc-950 text-zinc-100 sm:rounded-2xl"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-100">
              {t("assistant.resetTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {t("assistant.resetConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="ai-reset-cancel"
              className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100"
            >
              {tc("ui.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="ai-reset-confirm"
              onClick={onReset}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
            >
              {t("assistant.newChat")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* TourGuide — one-time KI coach mark */}
      {aiTour.eligible && !convQ.isLoading && (
        <SpotlightTour
          idPrefix="tour-ai"
          onFinish={aiTour.finish}
          doneLabel={tc("tour.got_it")}
          steps={[
            {
              target: isEmpty
                ? '[data-testid="ai-chat-quick-actions"]'
                : '[data-testid="ai-chat-input"]',
              title: tc("tour.ai.title"),
              body: tc("tour.ai.body"),
            },
          ]}
        />
      )}
    </div>
  );
}
